import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { db } from "../lib/db";
import { apiFetch, getCurrentPracticeId, getCurrentUserInfo, getAuthToken } from "../lib/api";

// ─── SESSION TIMEOUT ─────────────────────────────────────────────────────────
// Idle logout with a warning modal. Three hardening properties:
//   1. Cross-tab: last-activity lives in localStorage; activity in any tab
//      re-arms every tab (storage event), so an idle tab can't sign out a
//      user who is actively charting in another tab (fbSignOut is global).
//   2. Sleep/wake safe: timers are re-derived from the last-activity
//      TIMESTAMP on visibilitychange/focus — if the machine slept past the
//      deadline, logout fires immediately on wake instead of waiting for a
//      stale timer.
//   3. Throttled: pointermove fires continuously; activity is only recorded
//      every ACTIVITY_THROTTLE_MS unless the warning is showing.
const WARNING_BEFORE_MS      = 2 * 60 * 1000; // show warning 2 min before logout
const ACTIVITY_THROTTLE_MS   = 5 * 1000;
const LAST_ACTIVITY_KEY      = 'epiphanymd-last-activity';

export function useSessionTimeout(onTimeout) {
  const getTimeoutMs = () => {
    const mins = parseInt(localStorage.getItem('epiphanymd-session-timeout') || '15', 10);
    return (isNaN(mins) ? 15 : mins) * 60 * 1000;
  };

  const [showWarning, setShowWarning]   = useState(false);
  const [secondsLeft, setSecondsLeft]   = useState(120);
  const timeoutRef   = useRef(null);
  const warnRef      = useRef(null);
  const countRef     = useRef(null);
  const timeoutMs    = useRef(getTimeoutMs());
  const lastActivity = useRef(Date.now());
  const warnShowing  = useRef(false);

  const clear = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warnRef.current);
    clearInterval(countRef.current);
  };

  const getLastActivity = () => {
    let ts = lastActivity.current;
    try {
      const ls = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (!isNaN(ls) && ls > ts) ts = ls; // another tab may be more recent
    } catch { /* ignore */ }
    return ts;
  };

  // Re-derive all timers from the last-activity timestamp (NOT "now").
  const arm = useCallback(() => {
    clear();
    const remaining = timeoutMs.current - (Date.now() - getLastActivity());
    if (remaining <= 0) { onTimeout(); return; } // slept/idled past the deadline
    warnShowing.current = false;
    setShowWarning(false);
    const warnWindow = Math.min(WARNING_BEFORE_MS, remaining);
    warnRef.current = setTimeout(() => {
      warnShowing.current = true;
      setShowWarning(true);
      setSecondsLeft(Math.round(warnWindow / 1000));
      countRef.current = setInterval(() => {
        setSecondsLeft(s => (s <= 1 ? 0 : s - 1));
      }, 1000);
    }, remaining - warnWindow);
    timeoutRef.current = setTimeout(() => {
      clear();
      onTimeout();
    }, remaining);
  }, [onTimeout]);

  // Record activity (throttled) and re-arm. force bypasses the throttle
  // (used by the warning modal's Stay Logged In button).
  const recordActivity = useCallback((force = false) => {
    const now = Date.now();
    if (!force && !warnShowing.current && now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
    lastActivity.current = now;
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch { /* ignore */ }
    arm();
  }, [arm]);

  // Listen for settings change without a reload
  useEffect(() => {
    const handler = (e) => { timeoutMs.current = e.detail * 60 * 1000; arm(); };
    window.addEventListener('session-timeout-change', handler);
    return () => window.removeEventListener('session-timeout-change', handler);
  }, [arm]);

  useEffect(() => {
    const onActivity = () => recordActivity();
    const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'pointermove'];
    EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));

    // Activity in ANOTHER tab → re-arm this tab's timers (no LS write here).
    const onStorage = (e) => { if (e.key === LAST_ACTIVITY_KEY) arm(); };
    window.addEventListener('storage', onStorage);

    // Tab shown / window focused after sleep or backgrounding → re-derive
    // from the timestamp; logs out immediately if the deadline passed.
    const onVisible = () => { if (!document.hidden) arm(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    recordActivity(true);
    return () => {
      EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      clear();
    };
  }, [recordActivity, arm]);

  return { showWarning, secondsLeft, stayLoggedIn: () => recordActivity(true) };
}

export function SessionTimeoutModal({ secondsLeft, onStay }) {
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const label = mins > 0
    ? `${mins}m ${String(secs).padStart(2, '0')}s`
    : `${secondsLeft}s`;

  return createPortal(
    <div className="cmd-overlay" style={{ zIndex: 9999 }}>
      <div className="cmd-modal" style={{ width: 380, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '32px 28px 24px' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏱</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8 }}>
            Session Expiring
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 6, lineHeight: 1.6 }}>
            You'll be logged out due to inactivity in
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--teal)', marginBottom: 20, fontVariantNumeric: 'tabular-nums' }}>
            {label}
          </div>
          <button className="btn btn-primary" style={{ width: '100%', padding: '10px' }} onClick={onStay}>
            Stay Logged In
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── FEEDBACK BUTTON ─────────────────────────────────────────────────────────
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("General Feedback");
  const [area, setArea] = useState("Dashboard");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const TYPES = ["Bug", "Feature Request", "General Feedback"];
  const AREAS = ["Dashboard", "Patient Chart", "Notes", "Scheduling", "Billing", "ASC", "Portal", "Phone", "Messaging", "Other"];

  const reset = () => { setType("General Feedback"); setArea("Dashboard"); setDescription(""); setDone(false); };

  const handleOpen = () => { reset(); setOpen(true); };
  const handleClose = () => { setOpen(false); };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    const submittedAt = new Date().toISOString();
    const practiceId   = (await getCurrentPracticeId()) ?? null;
    const { uid, email } = await getCurrentUserInfo();
    const submittedBy  = email ?? uid ?? null;
    const authToken    = await getAuthToken();
    try {
      await db.from("feedback").insert([{
        feedback_type:  type,
        type,
        feature_area:   area,
        description:    description.trim(),
        practice_id:    practiceId,
        submitted_by:   submittedBy,
        status:         'new',
        screenshot_url: null,
      }]);
    } catch (e) {
      console.error("Feedback insert:", e);
    }
    // Email notification — fire-and-forget, don't block success UX on failure
    try {
      await apiFetch("send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify({
          subject: `Epiphany MD Feedback — ${type} — ${area}`,
          body: `<div style="font-family:sans-serif;max-width:560px;color:#1e293b">
  <h2 style="margin:0 0 16px;color:#0F6E56">New Feedback Submission</h2>
  <table style="width:100%;border-collapse:collapse;font-size:14px">
    <tr><td style="padding:8px 12px;background:#f1f5f9;font-weight:600;width:130px">Type</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0">${type}</td></tr>
    <tr><td style="padding:8px 12px;background:#f1f5f9;font-weight:600">Feature Area</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0">${area}</td></tr>
    <tr><td style="padding:8px 12px;background:#f1f5f9;font-weight:600">Submitted</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0">${new Date(submittedAt).toLocaleString()}</td></tr>
  </table>
  <div style="margin-top:16px;padding:14px;background:#f8fafc;border-left:3px solid #0F6E56;border-radius:4px;font-size:14px;line-height:1.6;white-space:pre-wrap">${description.trim().replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</div>
</div>`,
        }),
      });
    } catch (e) {
      console.error("Feedback email:", e);
    }
    setSubmitting(false);
    setDone(true);
  };

  // Phones: no floating FAB — the note editor is full-screen there and the
  // pill lands on top of Save/Follow-Up/Cancel (the editor's z-index is
  // trapped in a parent stacking context, so layering can't fix it).
  // Feedback stays available on tablet/desktop.
  if (typeof window !== "undefined" && window.innerWidth < 768) return null;

  return createPortal(
    <>
      {/* FAB */}
      <button
        onClick={handleOpen}
        style={{
          // zIndex 55: BELOW the note editor drawer (60), chart panels (59+),
          // and modals — so the FAB slides behind open work surfaces instead
          // of covering their footer buttons (Save Note / Follow-Up / Cancel).
          // Worst on phones where the editor is full-width.
          position: "fixed", bottom: 76, right: 20, zIndex: 55,
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 20,
          background: "var(--teal)", color: "#fff",
          border: "none", cursor: "pointer",
          fontSize: 12, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(13,148,136,0.45)",
          fontFamily: "var(--font)",
          transition: "opacity 0.15s, transform 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        💬 Feedback
      </button>

      {/* Modal */}
      {open && (
        <div className="cmd-overlay" onClick={handleClose}>
          <div className="cmd-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
              <div className="row">
                <span style={{ fontSize: 14, fontWeight: 600 }}>Send Feedback</span>
                <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={handleClose}>✕</button>
              </div>
            </div>

            {done ? (
              <div style={{ padding: "32px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)", marginBottom: 6 }}>Thank you!</div>
                <div style={{ fontSize: 13, color: "var(--text-2)" }}>Your feedback has been sent to Dr. Walker.</div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 20 }} onClick={handleClose}>Close</button>
              </div>
            ) : (
              <>
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="field">
                      <label className="field-label">Type</label>
                      <select className="input" value={type} onChange={e => setType(e.target.value)}>
                        {TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label className="field-label">Feature Area</label>
                      <select className="input" value={area} onChange={e => setArea(e.target.value)}>
                        {AREAS.map(a => <option key={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Description</label>
                    <textarea
                      className="input"
                      rows={5}
                      placeholder="Describe the bug, feature idea, or feedback…"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      style={{ resize: "vertical" }}
                      autoFocus
                    />
                  </div>
                </div>
                <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSubmit}
                    disabled={submitting || !description.trim()}>
                    {submitting ? "Sending…" : "Submit Feedback"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
